import { Dialog } from "@ark-ui/react/dialog";
import { Portal } from "@ark-ui/react/portal";
import { JSX, useCallback, useState } from "react";

import { backdropStyles, BaseModalProps, contentStyles, ownedOutsideHandlers } from "./shared";

export interface ModalProps extends BaseModalProps {
  /**
   * The element that should open the modal when clicked.
   * The element should be an HTML `button` that can accept a ref.
   * You may need to wrap your component in React.forwardRef.
   */
  trigger: JSX.Element;
  /**
   * When `true`, the dialog content will only be mounted when the modal is opened
   * and will unmount when the modal is closed.
   *
   * This is useful as a performance optimization for modals that have a lot of content or
   * content that is expensive to render.
   */
  mountOnlyWhenVisible?: boolean;
}

/** A component for opening a modal window that appears on top of the main content. */
export function Modal({
  trigger,
  renderContent,
  role = "dialog",
  dismissible = true,
  mountOnlyWhenVisible = false,
  onExitComplete,
  id,
  ownsElement,
}: ModalProps) {
  // Owned here rather than read back from Dialog.Context: Ark's `setOpen` is a fresh function
  // each render, and consumers put `close` in dependency arrays. One of them feeds it to a table
  // as a component type, where a new identity remounts every row instead of re-rendering it.
  const [open, setOpen] = useState(false);
  const close = useCallback(() => setOpen(false), []);

  return (
    <Dialog.Root
      id={id}
      open={open}
      onOpenChange={(e) => setOpen(e.open)}
      modal
      restoreFocus
      role={role}
      closeOnEscape={dismissible}
      closeOnInteractOutside={dismissible}
      {...ownedOutsideHandlers(ownsElement)}
      lazyMount={mountOnlyWhenVisible}
      unmountOnExit={mountOnlyWhenVisible}
      onExitComplete={onExitComplete}
    >
      <Dialog.Trigger asChild>{trigger}</Dialog.Trigger>
      <Portal>
        <Dialog.Backdrop className={backdropStyles} />
        <Dialog.Positioner>
          <Dialog.Content className={contentStyles} id={id ? `${id}-content` : undefined}>
            {renderContent({ close, isOpen: open })}
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
}
